import type { ModuleContext, CollectionEntityDefinition } from '@gzl10/nexus-sdk'
import { userEntity, roleEntity, userRoleEntity } from './users.entity.js'

// Table names from EntityDefinition
const USERS = (userEntity as CollectionEntityDefinition).table
const ROLES = roleEntity.table
const USER_ROLES = (userRoleEntity as CollectionEntityDefinition).table

/**
 * Seed for users: creates default roles and an admin user
 */
export async function seed(ctx: ModuleContext): Promise<void> {
  await seedRoles(ctx)
  await seedAdminUser(ctx)
  await seedDevUsers(ctx)
}

/**
 * Ensures system roles exist (inserts missing roles)
 */
async function seedRoles(ctx: ModuleContext): Promise<void> {
  const db = ctx.db.knex
  const { logger, generateId } = ctx.core

  // Skip if table doesn't exist (migrations not run yet)
  const hasTable = await db.schema.hasTable(ROLES)
  if (!hasTable) {
    logger.debug('roles table not found, skipping users seed')
    return
  }

  const roles = [
    { name: 'ADMIN', icon: 'mdi:shield-crown', description: { en: 'Administrator with full management', es: 'Administrador con gestión total' } },
    { name: 'EDITOR', icon: 'mdi:pencil', description: { en: 'Content management', es: 'Gestión de contenido' } },
    { name: 'USER', icon: 'mdi:account', description: { en: 'Basic user', es: 'Usuario básico' } },
    { name: 'VIEWER', icon: 'mdi:eye', description: { en: 'Read-only access', es: 'Solo lectura' } },
    { name: 'SUPPORT', icon: 'mdi:lifebuoy', description: { en: 'Technical support and diagnostics', es: 'Soporte técnico y diagnóstico' } }
  ]

  const inserted: string[] = []

  for (const role of roles) {
    const existing = await db(ROLES).where({ name: role.name }).first<{ id: string; icon?: string | null }>()

    if (!existing) {
      await db(ROLES).insert({
        id: generateId(),
        name: role.name,
        icon: role.icon,
        description: JSON.stringify(role.description)
      })
      inserted.push(role.name)
    } else if (!existing.icon) {
      // Backfill icon for roles seeded antes de añadir el campo
      await db(ROLES).where({ id: existing.id }).update({ icon: role.icon })
    }
  }

  if (inserted.length > 0) {
    logger.info({ roles: inserted }, 'Inserted missing roles')
  } else {
    logger.debug('All roles already exist')
  }
}

/**
 * Creates the initial admin user if it does not exist
 */
async function seedAdminUser(ctx: ModuleContext): Promise<void> {
  const db = ctx.db.knex
  const { logger, generateId, crypto: { hashPassword } } = ctx.core
  const config = ctx.config.resolved

  // Skip if table doesn't exist (migrations not run yet)
  const hasTable = await db.schema.hasTable(USERS)
  if (!hasTable) {
    return
  }

  const email = (config['adminEmail'] as string) || 'admin@example.com'
  const password = (config['adminPassword'] as string) || 'admin123'

  const existing = await db(USERS).where({ email }).first()
  if (existing) {
    logger.debug({ email }, 'Admin user already exists')
    return
  }

  // Get ADMIN role ID
  const adminRole = await db(ROLES).where({ name: 'ADMIN' }).first()
  if (!adminRole) {
    logger.error('ADMIN role not found. Run migrations first.')
    throw new Error('ADMIN role not found')
  }

  const hashedPassword = await hashPassword(password)
  const userId = generateId()

  // Create user without role_id
  await db(USERS).insert({
    id: userId,
    type: 'human',
    email,
    password: hashedPassword,
    name: 'Administrador',
    // GDPR: Admin user has pre-accepted consent for convenience
    consent_date: ctx.db.nowTimestamp(db),
    consent_version: '1.0',
    marketing_opt_in: false
  })

  // Assign ADMIN role via pivot table
  await db(USER_ROLES).insert({
    id: generateId(),
    user_id: userId,
    role_id: adminRole.id
  })

  logger.info({ email }, 'Admin user created with ADMIN role')
}

/**
 * Creates development users (one per role) only in development mode
 */
async function seedDevUsers(ctx: ModuleContext): Promise<void> {
  const db = ctx.db.knex
  const { logger, generateId, crypto: { hashPassword } } = ctx.core

  // Only create dev users in development mode
  if (process.env['NODE_ENV'] === 'production') return

  // Skip if table doesn't exist (migrations not run yet)
  const hasTable = await db.schema.hasTable(ROLES)
  if (!hasTable) {
    return
  }

  // Get all roles except ADMIN (admin user already seeded)
  const roles = await db(ROLES)
    .whereNot('name', 'ADMIN')
    .select('id', 'name')

  if (roles.length === 0) {
    logger.warn('No roles found, skipping dev users')
    return
  }

  // Create one user per role
  for (const role of roles) {
    const roleLower = role.name.toLowerCase()
    const email = `${roleLower}@dev.com`

    const existing = await db(USERS).where({ email }).first()
    if (existing) continue

    const userId = generateId()

    // Create user without role_id
    await db(USERS).insert({
      id: userId,
      type: 'human',
      email,
      password: await hashPassword(`${roleLower}123`),
      name: `${role.name.charAt(0)}${role.name.slice(1).toLowerCase()} User`,
      consent_date: null,
      consent_version: null,
      marketing_opt_in: false
    })

    // Assign role via pivot table
    await db(USER_ROLES).insert({
      id: generateId(),
      user_id: userId,
      role_id: role.id
    })

    logger.info({ email }, `Dev user created (${role.name})`)
  }
}
